<style scoped>
main {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.counts-bar {
	background-color: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 0.5rem;
	padding: 0.45rem 0.75rem;
	display: flex;
	align-items: center;
	gap: 1rem;
	flex-wrap: nowrap;
	flex-shrink: 0;
}
.progress {
	height: 0.5rem;
	flex: 1 1 auto;
	margin-bottom: 0;
	min-width: 4rem;
}
.step-scroll {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
}
.step-wrap {
	max-width: min(30rem, calc(100vw / var(--mc-scale, 1)));
	margin: 0 auto;
	width: 100%;
	display: flex;
	flex-direction: column;
}
.step-wrap :deep(.mc-card) {
	flex: 1 1 auto;
	min-height: 0;
}
.done-panel {
	text-align: center;
	padding: 2rem 1rem;
	max-width: min(30rem, calc(100vw / var(--mc-scale, 1)));
	margin: auto;
}
.done-icon {
	font-size: 3rem;
	color: #198754;
}
.mc-list {
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
}
.nav-row {
	flex-shrink: 0;
}
</style>
<template>
	<main>
		<div class="d-flex align-items-center justify-content-between mb-2 nav-row">
			<h2 class="h5 mb-0">{{ t("walk_title") }}</h2>
			<button
				type="button"
				class="btn btn-sm btn-outline-secondary"
				@click="toggleMode"
			>
				<i
					:class="mode === 'guided' ? 'bi-grid-3x3-gap' : 'bi-view-stacked'"
					class="me-1"
				></i>
				{{ mode === "guided" ? t("view_all") : t("guided") }}
			</button>
		</div>

		<div class="counts-bar mb-2">
			<span class="small fw-semibold">
				<i class="bi bi-check-circle-fill text-success me-1"></i>
				{{ t("counts", { confirmed: confirmedCount, clear: clearCount }) }}
			</span>
			<span v-if="unmarkedCount > 0" class="small text-muted">
				{{ t("step_progress", { remaining: unmarkedCount }) }}
			</span>
			<div class="progress">
				<div
					class="progress-bar"
					:style="{ width: progressPercent + '%' }"
					role="progressbar"
				></div>
			</div>
		</div>

		<!-- Guided: one habitat at a time -->
		<template v-if="mode === 'guided'">
			<div v-if="unmarkedCount === 0" class="done-panel card">
				<div class="card-body">
					<i class="bi bi-check-circle done-icon"></i>
					<h3 class="h5 mt-3">{{ t("done_title") }}</h3>
					<p class="text-muted mb-0">{{ t("done_body") }}</p>
					<button
						type="button"
						class="btn btn-outline-primary mt-3"
						@click="toggleMode"
					>
						<i class="bi bi-list-check me-1"></i>{{ t("review") }}
					</button>
				</div>
			</div>
			<template v-else>
				<div v-if="current" class="step-scroll">
					<div class="step-wrap">
						<HabitatCard
							:habitat="current"
							:token="props.token"
							large
							display-only
						/>
					</div>
				</div>
				<!-- Fixed detail tools: photo / larvae / already-fixed / remove -->
				<div v-if="current" class="nav-row mt-2">
					<HabitatTools :habitat="current" :token="props.token" />
				</div>
			</template>
		</template>

		<!-- List: all habitats in walk order, misc_water once at the end -->
		<div v-else class="mc-list">
			<div class="row g-2">
				<div
					v-for="habitat in orderedHabitats"
					:key="habitat.key"
					class="col-12 col-md-6 col-lg-4"
				>
					<HabitatCard :habitat="habitat" :token="props.token" />
				</div>
			</div>
		</div>

		<div class="d-flex gap-2 mt-2 nav-row">
			<button
				type="button"
				class="btn btn-outline-secondary"
				@click="onNavBack"
			>
				<i v-if="inWalk" class="bi bi-arrow-left me-1"></i>
				{{ t("back") }}
			</button>
			<button type="button" class="btn btn-primary" @click="onNavNext">
				{{ t("continue") }}<i v-if="inWalk" class="bi bi-arrow-right ms-1"></i>
			</button>
		</div>
	</main>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import { useT } from "@/rmo/content/mosquito/copy";
import HabitatCard from "@/rmo/content/mosquito/HabitatCard.vue";
import HabitatTools from "@/rmo/content/mosquito/HabitatTools.vue";
import { router } from "@/rmo/route/config";
import { useRoutes } from "@/rmo/route/use";
import { useStoreVirtualInspection } from "@/rmo/store/virtualinspection";
import type { VirtualInspectionHabitat } from "@/type/api";

// Walk order follows the design-spec taxonomy: most common first, misc last.
const HABITAT_ORDER = [
	"containers",
	"plant_saucer",
	"pet_animal_water",
	"bird_bath",
	"tires",
	"tarp_cover",
	"rain_gutter",
	"plant_axil",
	"pool_spa",
	"water_feature",
	"low_spots",
	"yard_drain",
	"appliance",
	"utility",
	"misc_water",
] as const;

interface Props {
	token: string;
}

const props = defineProps<Props>();
const routes = useRoutes();
const store = useStoreVirtualInspection();
const t = useT();

// Guided/list is a persisted device preference (see the store); the template
// and the nav computeds keep reading it as `mode`.
const mode = computed(() => store.mode);
const stepIndex = ref(0);

const orderedHabitats = computed<VirtualInspectionHabitat[]>(() => {
	const all = store.state?.habitats ?? [];
	return [...all].sort((a, b) => {
		const ia = HABITAT_ORDER.indexOf(a.key as (typeof HABITAT_ORDER)[number]);
		const ib = HABITAT_ORDER.indexOf(b.key as (typeof HABITAT_ORDER)[number]);
		if (ia === -1 && ib === -1) {
			return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
		}
		if (ia === -1) return 1;
		if (ib === -1) return -1;
		return ia - ib;
	});
});

function observationFor(habitat: VirtualInspectionHabitat) {
	return (
		store.state?.observations.find((o) => o.habitat_key === habitat.key) ?? null
	);
}

const unmarkedCount = computed(
	() => orderedHabitats.value.filter((h) => !observationFor(h)).length,
);
const confirmedCount = computed(
	() => store.state?.observations.filter((o) => o.present === true).length ?? 0,
);
const clearCount = computed(
	() =>
		store.state?.observations.filter((o) => o.present === false).length ?? 0,
);
const progressPercent = computed(() => {
	const total = orderedHabitats.value.length;
	return total === 0
		? 0
		: Math.round(((total - unmarkedCount.value) / total) * 100);
});

const current = computed(
	() =>
		orderedHabitats.value[
			Math.min(stepIndex.value, orderedHabitats.value.length - 1)
		] ?? null,
);
const currentObs = computed(() =>
	current.value ? observationFor(current.value) : null,
);

// Single contextual nav row: during the walk it steps one habitat at a time;
// in list mode it becomes screen navigation. Neither button is ever disabled —
// Continue always moves forward, Back always moves back, and the ends of the
// walk hand off to the neighbouring screens.
const lastStep = computed(() => Math.max(orderedHabitats.value.length - 1, 0));
const inWalk = computed(() => mode.value === "guided");

function onNavBack() {
	if (inWalk.value && stepIndex.value > 0) {
		stepIndex.value--;
		return;
	}
	goBack();
}

function onNavNext() {
	if (inWalk.value && stepIndex.value < lastStep.value) {
		stepIndex.value++;
		return;
	}
	goBeyond();
}

// Resume position: the first habitat the resident has not answered. Used when
// the walk starts and when returning to guided mode — never from a nav press,
// so a press can't jump the resident backwards.
function firstUnmarkedIndex(): number {
	const list = orderedHabitats.value;
	for (let i = 0; i < list.length; i++) {
		if (!observationFor(list[i])) return i;
	}
	return -1;
}

// Auto-advance when the resident marks the current habitat "not present".
// "I have this" stays put so the detail tasks (photo/larvae/fixed) can be
// completed; the Continue button advances instead.
watch(currentObs, (obs, prev) => {
	if (
		!prev &&
		obs &&
		obs.present === false &&
		stepIndex.value < lastStep.value
	) {
		stepIndex.value++;
	}
});

onMounted(() => {
	const idx = firstUnmarkedIndex();
	if (idx !== -1) {
		stepIndex.value = idx;
	}
});

function toggleMode() {
	if (mode.value === "list") {
		// Resume the guided walk at the first unanswered habitat.
		const idx = firstUnmarkedIndex();
		if (idx !== -1) {
			stepIndex.value = idx;
		}
		store.setMode("guided");
	} else {
		store.setMode("list");
	}
}

function goBack() {
	router.push(routes.MosquitoCheckWelcome(props.token));
}

function goBeyond() {
	router.push(routes.MosquitoCheckBeyond(props.token));
}
</script>
